import React from 'react';
import PropTypes from 'prop-types';

import { Helmet } from 'polaris-coreweb/exports';

import { getPrimaryMediaPreloadLink } from 'SharedPartialsLocal/getPrimaryMediaPreloadLink';

/**
 * Set Meta tag in the header
 * @returns {*}
 */
const getHelmetForReview = (props) => {
  const {
    layoutData: {
      page: pageData,
      page: {
        authors,
        teaserImage,
        created: datePublished,
        url,
      },
    },
    metaData: {
      pageTitle: metaTitle,
      pageDescription: metaDescription,
    },
    dataLayer: {
      product,
      productFamily,
      make,
    },
    config: {
      siteUrl,
    },
    context: {
      imageSizesCallback,
    },
  } = props;

  const image = teaserImage?.src ? [teaserImage.src] : [];

  const {
    rating,
    priceMin,
    priceMax,
    vehicleSpecList,
    body,
  } = pageData;

  // Extract the first sentence from the body.
  const getFirstSentenceFromBody = (bodyBlocks) => {
    if (!bodyBlocks || !Array.isArray(bodyBlocks)) return null;

    // Find the first text block - accept blocks with __typename === 'DataBlock' or blocks without __typename
    const firstTextBlock = bodyBlocks.find(
      block => block
               && block.type === 'TEXT'
               && block.data
               && (block.__typename === 'DataBlock' || !block.__typename),
    );

    if (!firstTextBlock) return null;

    // Safely coerce data to string only when data is present
    let textData = String(firstTextBlock.data);

    // Remove surrounding quotes only from actual string inputs
    if (typeof firstTextBlock.data === 'string') {
      textData = textData.replace(/^"|"$/g, '');
    }

    // Strip HTML tags and trim
    const plainText = textData.replace(/<[^>]*>/g, '').trim();

    if (!plainText) return null;

    // Extract first sentence using regex for common sentence terminators
    const sentenceMatch = plainText.match(/^[^.!?]*[.!?]+/);
    let firstSentence = sentenceMatch ? sentenceMatch[0].trim() : plainText.split(/[.!?]+\s+/)[0];

    if (!firstSentence) return null;

    // Ensure the returned sentence ends with a sentence terminator
    if (!/[.!?]$/.test(firstSentence)) {
      firstSentence += '.';
    }

    return firstSentence;
  };

  const getRating = (ratingValue) => {
    if (!ratingValue || ratingValue < 1) return null;

    return {
      '@type': 'Rating',
      bestRating: 5,
      worstRating: 1,
      ratingValue,
    };
  };

  // Get price range from various sources
  const getOffers = () => {
    const capPriceMin = vehicleSpecList?.priceRange?.capPriceMin;
    const capPriceMax = vehicleSpecList?.priceRange?.capPriceMax;

    const lowPrice = capPriceMin || priceMin;
    const highPrice = capPriceMax || priceMax;

    if (lowPrice && highPrice) {
      return {
        '@type': 'AggregateOffer',
        lowPrice,
        highPrice,
        priceCurrency: 'GBP',
      };
    }
    return null;
  };

  // Build Product schema as requested
  const reviewBody = getFirstSentenceFromBody(body);
  const rating_obj = getRating(rating);
  const offers = getOffers();

  const jsonld = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product || productFamily || make || metaTitle,
    description: metaDescription,
    ...(make && {
      brand: {
        '@type': 'Brand',
        name: make,
      },
    }),
    review: {
      '@type': 'Review',
      ...(authors && authors.length > 0 && {
        author: {
          '@type': 'Person',
          name: authors[0].name,
        },
      }),
      ...(datePublished && { datePublished }),
      ...(reviewBody && { reviewBody }),
      ...(rating_obj && { reviewRating: rating_obj }),
    },
    ...(offers && { offers }),
    ...(rating && {
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: rating,
        reviewCount: 1,
      },
    }),
    ...(url && { url: `${siteUrl}${url}` }),
    image,
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(jsonld)}</script>
      <title>{metaTitle}</title>
      <meta property="og:type" content="article" />

      {getPrimaryMediaPreloadLink(pageData, imageSizesCallback)}
    </Helmet>
  );
};

getHelmetForReview.propTypes = {
  layoutData: PropTypes.shape({
    page: PropTypes.shape({
      url: PropTypes.string,
      rating: PropTypes.number,
      priceMin: PropTypes.number,
      priceMax: PropTypes.number,
      vehicleSpecList: PropTypes.shape({
        priceRange: PropTypes.shape({
          capPriceMin: PropTypes.number,
          capPriceMax: PropTypes.number,
        }),
      }),
      authors: PropTypes.arrayOf(PropTypes.shape({
        name: PropTypes.string,
      })),
      teaserImage: PropTypes.shape({
        src: PropTypes.string,
      }),
      created: PropTypes.string,
      body: PropTypes.arrayOf(PropTypes.shape({
        __typename: PropTypes.string,
        type: PropTypes.string,
        data: PropTypes.string,
      })),
    }),
  }).isRequired,
  metaData: PropTypes.shape({
    pageTitle: PropTypes.string,
    pageDescription: PropTypes.string,
  }).isRequired,
  dataLayer: PropTypes.shape({
    product: PropTypes.string,
    productFamily: PropTypes.string,
    make: PropTypes.string,
  }).isRequired,
  config: PropTypes.shape({
    siteUrl: PropTypes.string,
  }).isRequired,
  context: PropTypes.shape({
    imageSizesCallback: PropTypes.func,
  }).isRequired,
};

export default getHelmetForReview;
